/**
 * Media tag system — API-backed data layer.
 *
 * Two pieces of state, both shared across every gallery/tile/lightbox via a tiny subscribable
 * store (React 17, so no useSyncExternalStore):
 *   - `vocabulary` — the tenant's tag list from GET /media-tags, loaded once and cached.
 *   - `assignments` — per media-item tag ids, hydrated from each media response's `tags` array
 *     and mutated optimistically by assign/unassign.
 *
 * Components read through the hooks in useMediaTags.js (useVocabulary / useTagsForItem); writes
 * go through toggleTagForItem, which calls the assign/unassign endpoints and reverts on failure.
 */
import { fetchConstructor } from '_entities/old/api/api';
import apiData from '_entities/old/api/config';
import { notification, idbService } from 'utils/services';
// Imported by path, not through the utils/custom barrel: this store is pulled in by every media
// surface and the barrel drags a lot (PDF export, parsers) along with it.
import { apiErrorMessage } from 'utils/custom/apiErrorMessage';
// HIDDEN (P20M-1319): uncomment with the concat in setVocabulary below to bring the mock Form tags back.
// import MOCK_FORM_TAGS from './mockFormTags';
import { isMockTag } from './mockFormTags';

const EMPTY = [];

// Normalize a media item's `tags` payload to a flat array of tag ids. Tolerates either full tag
// objects (`[{ id, name }]`) or bare ids (`[3, 7]`) — see the plan's shape caveat.
export const toTagIds = (tags) =>
  (tags || []).map((t) => (t && typeof t === 'object' ? t.id : t)).filter((id) => id != null);

// --- shared subscribable store -------------------------------------------------------------------
let version = 0;
const listeners = new Set();

const emit = () => {
  version += 1;
  listeners.forEach((listener) => listener());
};

export const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getVersion = () => version;

// --- vocabulary (GET /media-tags, loaded once) ---------------------------------------------------
let vocabulary = [];
let vocabularyById = {};
let vocabularyPromise = null;

const setVocabulary = (tags) => {
  // HIDDEN (P20M-1319): the mock form_question placeholders are parked until we have a real form.json
  // example — MOCK_FORM_TAGS is an empty list, so the vocabulary is exactly what the API returned and the
  // filter's "Form tags" group disappears. Uncomment both lines below (and the list in mockFormTags) to
  // get the preview back; the placeholders yield to a real tag of the same name.
  // const realNames = new Set(tags.map((tag) => tag.name));
  // vocabulary = [...tags, ...MOCK_FORM_TAGS.filter((tag) => !realNames.has(tag.name))];
  vocabulary = tags;
  vocabularyById = vocabulary.reduce((acc, tag) => {
    acc[tag.id] = tag;
    return acc;
  }, {});
};

export const getVocabulary = () => vocabulary;
export const getTagById = (id) => vocabularyById[id];

// A tag is "custom" (user-created, editable/deletable) when its source is manual; anything else
// (e.g. form_question) is a locked "system" tag. `source` is what the backend validates against, so it
// stays the predicate for the rename/delete controls and the System/Custom grouping.
export const isCustomTag = (tag) => tag?.source === 'manual';

// Whether a tag is bound to a form's file question: those carry `form: <id>`, user-created ones
// `form: null`. Only used to pick the tag's colours (see tagPalette) — the backend clears `form` for
// manual tags, so this always agrees with isCustomTag, but it keeps the palette keyed to the field the
// payload actually varies.
export const isFormLinkedTag = (tag) => tag?.form != null;

// --- context-scoped tag counts (from the gallery responses) --------------------------------------
// The tag-filter popup must show counts for the gallery the user is looking at (visit type /
// retailer scope, list filters, the Images/Documents tab), so every gallery response now carries
// `tag_counts: {"<tag_id>": n}` computed server-side over its own filtered context (facet
// semantics — the `tags=` filter itself is excluded). Each gallery fetch stores its map here;
// null until the first gallery response lands, at which point counts fall back to the tenant-wide
// `media_count` from GET /media-tags rather than showing nothing.
let contextTagCounts = null;

export const setContextTagCounts = (counts) => {
  // Older backends / unrelated fetches carry no map — keep whatever we had rather than degrading
  // already-correct numbers to the global fallback.
  if (!counts || typeof counts !== 'object') return;
  contextTagCounts = counts;
  emit();
};

// Per-tag media count for the popup. Context-scoped when a gallery has reported its counts (a tag
// missing from the map has 0 media in that context); before that, the tenant-wide `media_count`
// from GET /media-tags; null hides the number entirely (mock form tags — no invented counts).
export const getTagCount = (tag) => {
  if (isMockTag(tag)) return null;
  if (contextTagCounts) return contextTagCounts[String(tag?.id)] ?? 0;
  const count = tag?.count ?? tag?.media_count ?? tag?.usage_count;
  return count == null ? null : count;
};

// Mirror of the fetched vocabulary in IndexedDB, so the survey form can still show tag names and offer
// the picker with no network — the create-survey flow is filled offline by design. Best-effort in both
// directions: a storage failure must never take the online path down with it.
const cacheVocabulary = async (tags) => {
  try {
    await idbService.putMediaTags(tags);
  } catch (e) {
    // Storage unavailable/full — the in-memory vocabulary still serves this session.
  }
};

const loadCachedVocabulary = async () => {
  try {
    return await idbService.getMediaTags();
  } catch (e) {
    return null;
  }
};

// Fetch the tag vocabulary at most once (memoized behind a single in-flight promise). Safe to call
// from many components on mount — later callers reuse the same request.
//
// When the request fails — offline, or the SW passing an unhandled API route through — the last cached
// copy is used instead, so tags work offline exactly as they do online. A failed load still clears the
// memoised promise, so coming back online re-fetches rather than serving the cache all session.
export const loadVocabulary = () => {
  if (vocabularyPromise) return vocabularyPromise;

  const useCache = async () => {
    vocabularyPromise = null;
    // Don't overwrite a vocabulary already in memory with an older cached copy.
    if (vocabulary.length) return vocabulary;
    const cached = await loadCachedVocabulary();
    if (cached?.length) {
      setVocabulary(cached);
      emit();
    }
    return vocabulary;
  };

  vocabularyPromise = fetchConstructor('get', apiData.mediaTags)
    .then((response) => {
      if (response && response.status >= 200 && response.status < 300 && response.data) {
        // Bare list or DRF-paginated { results: [...] } — tolerate both.
        const tags = response.data.results || response.data || [];
        setVocabulary(tags);
        emit();
        cacheVocabulary(tags);
        return vocabulary;
      }
      return useCache();
    })
    .catch(useCache);
  return vocabularyPromise;
};

// Drop the memoised promise and hit GET /media-tags again. Used after a create/rename/delete when the
// mutation response doesn't carry the full object, and whenever a surface needs the per-tag `media_count`
// to be current — those counts drift as soon as anyone tags a file, and loadVocabulary() only ever
// fetches once per session.
export const reloadVocabulary = () => {
  vocabularyPromise = null;
  return loadVocabulary();
};

const isOk = (response) => response && response.status >= 200 && response.status < 300;

// --- vocabulary CRUD (requires manage_media_tags on the backend) ---------------------------------
// Create a manual tag. On success returns the created tag (updating the shared vocabulary); null on
// failure. Both outcomes toast: vocabulary edits are easy to miss in a popover full of checkboxes, so
// each one gets the same confirmation as the delete.
export const createTag = async (name) => {
  const response = await fetchConstructor('post', apiData.mediaTags, undefined, { name });
  if (!isOk(response)) {
    // e.g. { name: ["A tag with this name already exists."] } — far more use than "Failed to create tag".
    notification.error(apiErrorMessage(response, 'Failed to create tag'));
    return null;
  }
  const created = response.data && response.data.id != null ? response.data : null;
  if (created) {
    setVocabulary([...vocabulary, created]);
    emit();
  } else {
    await reloadVocabulary(); // response didn't include the new tag — refresh the list.
  }
  notification.success(`Tag “${name}” created`);
  return created || getVocabulary().find((t) => t.name === name) || null;
};

// Rename a tag. Returns true on success.
export const renameTag = async (id, name) => {
  const response = await fetchConstructor('patch', `${apiData.mediaTags}/${id}`, undefined, { name });
  if (!isOk(response)) {
    notification.error(apiErrorMessage(response, 'Failed to rename tag'));
    return false;
  }
  setVocabulary(vocabulary.map((tag) => (tag.id === id ? { ...tag, ...(response.data || {}), name } : tag)));
  emit();
  notification.success(`Tag renamed to “${name}”`);
  return true;
};

// Delete a tag and strip it from any loaded per-item assignments. Returns true on success.
export const deleteTag = async (id) => {
  // Captured before the row leaves the vocabulary, so the toast can name it.
  const deletedName = getTagById(id)?.name;
  const response = await fetchConstructor('delete', `${apiData.mediaTags}/${id}`);
  if (!isOk(response)) {
    notification.error(apiErrorMessage(response, 'Failed to delete tag'));
    return false;
  }
  setVocabulary(vocabulary.filter((tag) => tag.id !== id));
  const nextAssignments = {};
  Object.keys(assignments).forEach((key) => {
    nextAssignments[key] = assignments[key].filter((tagId) => tagId !== id);
  });
  assignments = nextAssignments;
  emit();
  notification.success(deletedName ? `Tag “${deletedName}” deleted` : 'Tag deleted');
  return true;
};

// --- per-item assignments ------------------------------------------------------------------------
// { [mediaId: string]: tagId[] }. Hydrated from media responses; server is the source of truth.
let assignments = {};

export const getTagsForItem = (id) => {
  if (id == null) return EMPTY;
  const key = String(id);
  return Object.prototype.hasOwnProperty.call(assignments, key) ? assignments[key] : EMPTY;
};

const setTagsForItem = (id, tagIds) => {
  if (id == null) return;
  assignments = { ...assignments, [String(id)]: tagIds };
  emit();
};

// Seed the store from a page of media items (each may carry a `tags` array). Idempotent per page:
// only re-emits when something actually changed, so calling it from an effect on every `items`
// change doesn't loop.
export const hydrate = (items = []) => {
  let changed = false;
  const next = { ...assignments };
  items.forEach((item) => {
    if (!item || item.id == null || item.tags == null) return;
    const key = String(item.id);
    const ids = toTagIds(item.tags);
    const prev = next[key];
    if (!prev || prev.length !== ids.length || ids.some((id, i) => id !== prev[i])) {
      next[key] = ids;
      changed = true;
    }
  });
  if (changed) {
    assignments = next;
    emit();
  }
};

// One entry per (media, tag) pair currently awaiting a response. Keyed per pair, not per media item,
// so tagging one file with several tags in quick succession stays fully parallel.
const inFlight = new Set();
const flightKey = (id, tagId) => `${id}:${tagId}`;

// Whether a toggle for this pair is still awaiting its response — for surfaces that want to show it.
export const isTagPending = (id, tagId) => inFlight.has(flightKey(id, tagId));

// Keep the popup's context count in step with an optimistic assignment toggle. The toggled media
// item is by definition inside the current gallery, so ±1 mirrors what the next gallery refetch
// would report anyway.
const bumpContextCount = (tagId, delta) => {
  if (!contextTagCounts) return;
  const key = String(tagId);
  contextTagCounts = {
    ...contextTagCounts,
    [key]: Math.max(0, (contextTagCounts[key] ?? 0) + delta),
  };
  emit();
};

/**
 * Optimistically toggle one tag on one media item, then persist via assign/unassign. On failure the
 * optimistic change is undone and an error toast is shown.
 *
 * Two things here exist for keyboard tagging, where this gets called far faster than requests return:
 *
 *  - A re-entrant call for the same pair is DROPPED rather than queued. Two presses inside one
 *    round-trip would otherwise race an assign against an unassign: the UI would show whichever was
 *    optimistic last while the server kept whichever landed last, and they can disagree permanently.
 *  - The failure path undoes only THIS tag, against the array as it stands at that moment. Restoring
 *    a snapshot taken before the request (what this used to do) wiped any other tag toggled
 *    successfully while the request was in flight.
 */
export const toggleTagForItem = async (id, tagId) => {
  const key = flightKey(id, tagId);
  if (inFlight.has(key)) return;
  inFlight.add(key);

  const current = getTagsForItem(id);
  const willAssign = !current.includes(tagId);
  setTagsForItem(id, willAssign ? [...current, tagId] : current.filter((t) => t !== tagId));
  bumpContextCount(tagId, willAssign ? 1 : -1);

  let ok = false;
  // Declared out here so the failure branch below can quote what the server actually said.
  let response = null;
  try {
    const url = willAssign ? apiData.mediaTagsAssign : apiData.mediaTagsUnassign;
    response = await fetchConstructor('post', url, undefined, {
      media_ids: [id],
      tag_ids: [tagId],
    });
    ok = isOk(response);
  } catch {
    // A network throw used to escape as an unhandled rejection, leaving the optimistic state applied
    // with no toast at all.
    ok = false;
  } finally {
    inFlight.delete(key);
  }

  if (!ok) {
    const now = getTagsForItem(id);
    const reverted = willAssign ? now.filter((t) => t !== tagId) : now.includes(tagId) ? now : [...now, tagId];
    setTagsForItem(id, reverted);
    bumpContextCount(tagId, willAssign ? -1 : 1);
    // Shared toastId so hammering a key offline doesn't stack identical toasts.
    notification.error(
      apiErrorMessage(response, willAssign ? 'Failed to add tag' : 'Failed to remove tag'),
      undefined,
      {
        toastId: `media-tag-fail-${tagId}`,
      },
    );
  }
};
